import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminListServices, adminCreateService, adminUpdateService } from "@/lib/api";
import { ArrowLeft, Save } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";

const EMPTY = {
  slug: "", kind: "pooja", name: "", devanagari_name: "", description: "", long_description: "",
  deity: "", occasion: "", duration: "", includes: [], suggested_offering: "", image_url: "", featured: false,
};
const inputCls = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const lbl = "mb-1 block text-xs uppercase tracking-wider text-ink-3";

export default function AdminServiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(!editing);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) adminListServices().then((items) => {
      const f = items.find((x) => x.id === id);
      if (f) setForm({ ...EMPTY, ...f });
      setLoaded(true);
    });
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await adminUpdateService(id, form);
      else await adminCreateService(form);
      toast("Service saved", { description: form.name });
      navigate("/admin/services");
    } catch (err) {
      toast.error("Save failed", { description: err.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  if (!loaded) return <p className="py-16 text-center text-ink-3">…</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <button onClick={() => navigate("/admin/services")} className="mb-6 inline-flex items-center gap-2 text-sm text-ink-2 hover:text-saffron"><ArrowLeft className="h-4 w-4" /> Back</button>
      <h1 className="mb-8 font-serif-latin text-3xl text-ink">{editing ? "Edit Service" : "New Service"}</h1>
      <div className="space-y-4 border border-line bg-surface p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Kind</label><select value={form.kind} onChange={(e) => set("kind", e.target.value)} className={inputCls} data-testid="service-kind"><option value="pooja">Pooja</option><option value="yagya">Yagya</option></select></div>
          <div><label className={lbl}>Deity</label><input value={form.deity} onChange={(e) => set("deity", e.target.value)} className={inputCls} /></div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Devanagari name</label><input value={form.devanagari_name} onChange={(e) => set("devanagari_name", e.target.value)} className={`${inputCls} font-deva-body`} /></div>
          <div><label className={lbl}>Name (English)</label><input data-testid="service-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
        </div>
        <div><label className={lbl}>Short description</label><textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></div>
        <div><label className={lbl}>Long description</label><textarea rows={4} value={form.long_description} onChange={(e) => set("long_description", e.target.value)} className={inputCls} /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Occasion</label><input value={form.occasion} onChange={(e) => set("occasion", e.target.value)} className={inputCls} /></div>
          <div><label className={lbl}>Duration</label><input value={form.duration} onChange={(e) => set("duration", e.target.value)} className={inputCls} placeholder="1–1.5 hours" /></div>
        </div>
        <div><label className={lbl}>Includes (one per line)</label><textarea rows={4} value={form.includes.join("\n")} onChange={(e) => set("includes", e.target.value.split("\n").filter((x) => x.trim()))} className={inputCls} /></div>
        <div><label className={lbl}>Suggested offering note</label><input value={form.suggested_offering} onChange={(e) => set("suggested_offering", e.target.value)} className={inputCls} /></div>
        <div><ImageUploader label="Image" value={form.image_url} onChange={(v) => set("image_url", v)} testid="service-image" /></div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[var(--saffron)]" /><span className="text-sm text-ink-2">Featured</span></label>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={() => navigate("/admin/services")} className="px-5 py-2.5 text-sm text-ink-2">Cancel</button>
          <button data-testid="service-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
